import { z } from 'zod';

import { ChatChannelSchema } from '../card/card';
import { ContactRefSchema } from './contact_ref';
import { EnvelopeOrgIdSchema } from './org_id';

export const MessageIdSchema = z.guid().brand<'MessageId'>();
export type MessageId = z.infer<typeof MessageIdSchema>;

export const ConversationIdSchema = z.guid().brand<'ConversationId'>();
export type ConversationId = z.infer<typeof ConversationIdSchema>;

export const MessageDirectionSchema = z.enum(['inbound', 'outbound']);
export type MessageDirection = z.infer<typeof MessageDirectionSchema>;

export const AttachmentSchema = z
  .object({
    url: z.url(),
    mimeType: z.string().max(100),
    sizeBytes: z.number().int().positive().optional(),
    caption: z.string().max(1024).optional(),
  })
  .strict();
export type Attachment = z.infer<typeof AttachmentSchema>;

/**
 * Normalized cross-channel identity signal. Each channel fills it with its
 * best lookup-able attribute: WhatsApp → phone (E.164), Slack → email
 * (resolved via `users.info`), the authenticated app channel → id (the numeric
 * `business.users.id` decrypted from the app token). Optional — producers OMIT
 * it when no identity signal is available (fail-open). This is the lookup key
 * ("who is this"), deliberately distinct from `contactRef.externalId` (the
 * channel ADDRESS, used for threading + outbound and intentionally left stable
 * per channel).
 */
export const SenderIdentityKindSchema = z.enum(['phone', 'email', 'id']);
export type SenderIdentityKind = z.infer<typeof SenderIdentityKindSchema>;

export const SenderIdentitySchema = z
  .object({
    kind: SenderIdentityKindSchema,
    value: z.string().min(1).max(320),
  })
  .strict();
export type SenderIdentity = z.infer<typeof SenderIdentitySchema>;

/**
 * Common envelope fields. Spread (`...EnvelopeBaseShape`) into each
 * variant rather than `.extend()`-ed onto a base object — Zod's
 * `discriminatedUnion` inspects the top-level object literal, so each
 * variant must be its own top-level `z.object({...}).strict()`.
 */
const EnvelopeBaseShape = {
  envelope: z.object({ version: z.literal(1) }).strict(),
  messageId: MessageIdSchema,
  externalMessageId: z.string().min(1).max(200),
  channel: ChatChannelSchema,
  contactRef: ContactRefSchema,
  senderIdentity: SenderIdentitySchema.optional(),
  orgId: EnvelopeOrgIdSchema,
  timestamp: z.coerce.date(),
  direction: MessageDirectionSchema,
  attachments: z.array(AttachmentSchema).max(20).default([]),
  channelMetadata: z.record(z.string(), z.unknown()).default({}),
  // Thread alvo p/ canais que roteiam por id explícito (canal app, múltiplas
  // conversas ativas). Outros canais omitem → threading por contato (getOrCreate).
  targetConversationId: ConversationIdSchema.optional(),
  // Trip a que a conversa pertence (CONV-02). App-oriented como targetConversationId:
  // só o canal app stampa; outros canais omitem. Contexto de trip, não id de conversa
  // (por isso string simples, não ConversationIdSchema). Persistido set-if-absent.
  tripId: z.string().min(1).max(100).optional(),
  // The channel message this inbound quotes (WhatsApp `context.id`). Channels
  // without reply threading omit it. `conversationId` is the conversation the
  // quoted OUTBOUND message was sent from, when the connector recorded it
  // (proactive notifications) — a routing hint, never an authorization: the
  // consumer re-checks that it belongs to this contact.
  replyTo: z
    .object({
      externalMessageId: z.string().min(1).max(200),
      conversationId: ConversationIdSchema.optional(),
    })
    .strict()
    .optional(),
} as const;

/** Portão do corpo vazio: exige uma confirmation ESTRUTURADA, não só a chave
 *  presente. `channelMetadata` é `Record<string, unknown>`, então `in` sozinho
 *  deixaria passar `null`, `false`, `0` e `{}` — um corpo vazio sem challenge
 *  real. Só o `challengeId` é checado aqui (o gate); a forma completa é
 *  validada por `AppChannelConfirmationSchema` no app-integration, que não pode
 *  ser importado aqui (ele já importa deste módulo — seria ciclo). */
const EmptyBodyConfirmationGateSchema = z.object({ challengeId: z.string().min(1) });

export const TextMessageEnvelopeSchema = z
  .object({
    ...EnvelopeBaseShape,
    messageType: z.literal('text'),
    payload: z.object({ body: z.string().max(10_000) }).strict(),
  })
  .strict()
  // Corpo vazio é EXCLUSIVO do requery silencioso do canal app (interação de
  // card com `confirmation` estruturada em channelMetadata — ex.: troca de
  // filtro no hotel_planner). Os demais canais mantêm a guarda de corpo
  // não-vazio de sempre.
  .refine(
    (v) =>
      v.payload.body.length > 0 ||
      (v.channel === 'app' &&
        EmptyBodyConfirmationGateSchema.safeParse(v.channelMetadata.confirmation).success),
    { message: 'empty body is only valid for app-channel card confirmations', path: ['payload'] },
  );
export type TextMessageEnvelope = z.infer<typeof TextMessageEnvelopeSchema>;

export const ImageMessageEnvelopeSchema = z
  .object({
    ...EnvelopeBaseShape,
    messageType: z.literal('image'),
    payload: z
      .object({
        url: z.url(),
        mimeType: z.string().max(100).optional(),
        caption: z.string().max(1024).optional(),
      })
      .strict(),
  })
  .strict();
export type ImageMessageEnvelope = z.infer<typeof ImageMessageEnvelopeSchema>;

export const AudioMessageEnvelopeSchema = z
  .object({
    ...EnvelopeBaseShape,
    messageType: z.literal('audio'),
    payload: z
      .object({
        url: z.url(),
        mimeType: z.string().max(100).optional(),
        caption: z.string().max(1024).optional(),
      })
      .strict(),
  })
  .strict();
export type AudioMessageEnvelope = z.infer<typeof AudioMessageEnvelopeSchema>;

export const VideoMessageEnvelopeSchema = z
  .object({
    ...EnvelopeBaseShape,
    messageType: z.literal('video'),
    payload: z
      .object({
        url: z.url(),
        mimeType: z.string().max(100).optional(),
        caption: z.string().max(1024).optional(),
      })
      .strict(),
  })
  .strict();
export type VideoMessageEnvelope = z.infer<typeof VideoMessageEnvelopeSchema>;

export const DocumentMessageEnvelopeSchema = z
  .object({
    ...EnvelopeBaseShape,
    messageType: z.literal('document'),
    payload: z
      .object({
        url: z.url(),
        mimeType: z.string().max(100).optional(),
        filename: z.string().min(1).max(200),
        caption: z.string().max(1024).optional(),
      })
      .strict(),
  })
  .strict();
export type DocumentMessageEnvelope = z.infer<typeof DocumentMessageEnvelopeSchema>;

export const LocationMessageEnvelopeSchema = z
  .object({
    ...EnvelopeBaseShape,
    messageType: z.literal('location'),
    payload: z
      .object({
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
        name: z.string().max(200).optional(),
        address: z.string().max(500).optional(),
      })
      .strict(),
  })
  .strict();
export type LocationMessageEnvelope = z.infer<typeof LocationMessageEnvelopeSchema>;

export const MessageEnvelopeSchema = z.discriminatedUnion('messageType', [
  TextMessageEnvelopeSchema,
  ImageMessageEnvelopeSchema,
  AudioMessageEnvelopeSchema,
  VideoMessageEnvelopeSchema,
  DocumentMessageEnvelopeSchema,
  LocationMessageEnvelopeSchema,
]);

export type MessageEnvelope = z.infer<typeof MessageEnvelopeSchema>;
